/**
 * Dependencies.
 */
import { authService } from '@prz-dp/services';
import { AxiosRequestConfig } from 'axios';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import { ServicePointModel } from '@/api/service-point/service-point/service-point.model';
import { servicePointService } from '@/api/service-point/service-point/service-point.service';
import HeadpieceConponent from '@/modules/service-point/components/headpiece/headpiece.component.vue';
import { IUser } from '@/ui/components/user-menu/user-menu.types';

/**
 * Component.
 */
@Component({
  components: { headpiece: HeadpieceConponent },
  name: 'service-point-scene'
})
export default class extends Vue {
  /**
   * Property `servicePoint`.
   */
  servicePoint: ServicePointModel = null;

  /**
   * Property `pickUpPoints`.
   */
  pickUpPoints: ServicePointModel[] = [];

  /**
   * Property `pickupPointsPage`.
   */
  pickupPointsPage = 1;

  /**
   * Property `isModalWithoutServicePointVisible`.
   */
  isModalWithoutServicePointVisible = false;

  /**
   * Getter `user`.
   */
  get user(): IUser {
    const { keycloakProfile } = this.$kit?.auth || {};
    const { attributes, email = '', firstName = '', lastName = '' } =
      keycloakProfile || {};
    const { prozisUserId = [] } = attributes || {};
    const [id = null] = prozisUserId;
    const name = [firstName, lastName].filter(Boolean).join(' ');

    return {
      email,
      initials: `${firstName?.charAt(0)}${lastName?.charAt(0)}`,
      name,
      prozisUserId: id
    };
  }

  /**
   * Getter `servicePointId`.
   */
  get servicePointId() {
    const { id } = this.servicePoint || {};

    return id;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleServicePointHeaders();
    this.handleInitialize();
  }

  /**
   * Handle initialize.
   */
  async handleInitialize() {
    await this.handleGetServicePointInformation();
  }

  /**
   * Handle get service point information.
   */
  async handleGetServicePointInformation({
    page,
    searchString = ''
  }: {
    page?: number;
    searchString?: string;
  } = {}) {
    try {
      if (page) {
        this.pickupPointsPage = page;
      }

      const response = await servicePointService.getFromUser({
        page: this.pickupPointsPage,
        searchString
      });

      if (response?.length) {
        if (page === 1) {
          this.pickUpPoints = [...(response || [])];
        } else {
          this.pickUpPoints = [...this.pickUpPoints, ...(response || [])];
        }

        this.pickupPointsPage++;
      }

      if (!this.servicePoint) {
        const [firstItem] = response || [];

        this.servicePoint = firstItem ? { ...firstItem } : null;
        this.isModalWithoutServicePointVisible = !firstItem ? true : false;
      }
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetServicePointInformation',
          scene: 'ServicePointScene'
        });
      }
    }
  }

  /**
   * Handle service point headers.
   */
  handleServicePointHeaders() {
    this.$kit.http.addRequestInterceptor((config: AxiosRequestConfig) => {
      const { code: servicePointCode = null } = this.servicePoint || {};

      if (servicePointCode) {
        config.headers = {
          ...config.headers,
          ServicePointCode: servicePointCode
        };
      }

      return config;
    });
  }

  /**
   * Handle without service point.
   */
  handleWithoutServicePoint() {
    this.handleLogout();
  }

  /**
   * Handle selected new pick up point.
   */
  async handleSelectedNewPickUpPoint({ pickUpPoint }) {
    this.servicePoint = { ...pickUpPoint };
  }

  /**
   * Handle logout.
   */
  async handleLogout() {
    const redirectUri = window.location.href
      .split('/')
      .slice(0, 3)
      .join('/');

    authService.logout({ redirectUri: `${redirectUri}/service-point` });
  }

  /**
   * Handle load more pickup points.
   */
  handleLoadMorePickUpPoints(data) {
    this.handleGetServicePointInformation(data);
  }
}
